import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Typography, CircularProgress, InputAdornment, Button } from '@mui/material';
import dayjs from '../../utils/dayjsConfig';
import { StyledAvatar } from '../StyledAvatar/StyledAvatar';
import { CheckmarkIcon } from '../../assets/svg';
import { DrawerContentContainer } from '../AdminDrawer/AdminDrawer.styles';
import { useVenueTimeFormatters } from '../../utils/Hooks/venue/useVenueTimeFormatters';
import { handleListItemKeyDown } from '../../utils/helpers/accessibility/keyboardHelpers';
import {
  EmployeeSection,
  EmployeeAvatar,
  EmployeeName,
  EmployeeRoles,
  SectionLabel,
  PaymentMethodSection,
  PaymentMethodList,
  PaymentMethodOption,
  PaymentDetailsSection,
  PaymentDetailsList,
  DetailRow,
  DateValue,
  AmountInputContainer,
  AmountInput,
  ErrorContainer,
  ErrorText,
  LoadingContainer,
} from './RecordPaymentDrawer.styles';

const PAYMENT_METHODS = [
  { id: 'cash', label: 'Cash' },
  { id: 'venmo', label: 'Venmo' },
  { id: 'zelle', label: 'Zelle' },
  { id: 'direct deposit', label: 'Direct Deposit' },
];

const RecordPaymentDrawer = ({ employee, onConfirm, handleCloseDrawer }) => {
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const { formatDate: formatVenueDate, venueTimezone } = useVenueTimeFormatters();

  // Initialize date to today (in venue timezone) and prefill payment amount with tips owed
  useEffect(() => {
    // Get today's date in venue timezone
    const todayInVenueTimezone = dayjs().tz(venueTimezone);
    setPaymentDate(todayInVenueTimezone);

    // Convert tips owed from cents to dollars and prefill
    const tipsOwedInDollars = ((employee.tipsOwed || 0) / 100).toFixed(2);
    setPaymentAmount(tipsOwedInDollars);
  }, [employee.id, employee.tipsOwed, venueTimezone]);

  const handlePaymentAmountChange = (e) => {
    const value = e.target.value;
    // Allow only numbers and decimal point
    const numericValue = value.replace(/[^0-9.]/g, '');

    // Prevent multiple decimal points
    const parts = numericValue.split('.');
    if (parts.length > 2) {
      return;
    }

    // Limit to 2 decimal places
    if (parts[1] && parts[1].length > 2) {
      return;
    }

    setPaymentAmount(numericValue);
    setError('');
  };

  const handlePaymentMethodSelect = (methodId) => {
    if (!isLoading) {
      setPaymentMethod(methodId);
      setError('');
    }
  };

  const handleSaveClick = async () => {
    const amount = parseFloat(paymentAmount);

    if (!paymentAmount || isNaN(amount) || amount <= 0) {
      setError('Please enter a valid payment amount');
      return;
    }

    // Convert tipsOwed from cents to dollars for comparison
    const tipsOwedInDollars = (employee.tipsOwed || 0) / 100;
    if (amount > tipsOwedInDollars) {
      setError('Payment amount cannot exceed outstanding tips');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Format date as YYYY-MM-DD for backend
      const formattedDate = paymentDate.format('YYYY-MM-DD');
      await onConfirm(amount, paymentMethod, formattedDate);
      // Close drawer on success
      handleCloseDrawer();
    } catch (err) {
      // Error will be set by the parent component's onConfirm
      setError(err.message || 'Failed to process payment. Please try again.');
      setIsLoading(false);
    }
  };

  // Format date for display (e.g., "Mon, Jan 05, 2026") in venue timezone
  const formatDateForDisplay = (date) => {
    if (!date) return '';
    return formatVenueDate(date.toDate(), {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: '2-digit',
    });
  };

  return (
    <DrawerContentContainer alignment="stretch">
      {/* Employee Section */}
      <EmployeeSection>
        <EmployeeAvatar>
          <StyledAvatar size="large" alt={employee.displayName || 'User'} src={employee.profileImageUrl} />
        </EmployeeAvatar>
        <EmployeeName variant="h5">{employee.displayName}</EmployeeName>
        <EmployeeRoles variant="body2">
          {employee.shiftTypes && employee.shiftTypes.length > 0 ? employee.shiftTypes.join(', ') : 'Employee'}
        </EmployeeRoles>
      </EmployeeSection>

      {/* Payment Method Section */}
      <PaymentMethodSection>
        <SectionLabel variant="subtitle2" id="payment-method-label">
          PAYMENT METHOD
        </SectionLabel>
        <PaymentMethodList role="radiogroup" aria-labelledby="payment-method-label">
          {PAYMENT_METHODS.map((method, index) => (
            <PaymentMethodOption
              key={method.id}
              role="radio"
              aria-checked={paymentMethod === method.id}
              selected={paymentMethod === method.id}
              onClick={() => handlePaymentMethodSelect(method.id)}
              onKeyDown={handleListItemKeyDown(
                () => handlePaymentMethodSelect(method.id),
                index,
                PAYMENT_METHODS.length,
                isLoading
              )}
              disabled={isLoading}
              isLast={index === PAYMENT_METHODS.length - 1}
              tabIndex={paymentMethod === method.id ? 0 : -1}
            >
              <Typography variant="h6">{method.label}</Typography>
              {paymentMethod === method.id && <CheckmarkIcon />}
            </PaymentMethodOption>
          ))}
        </PaymentMethodList>
      </PaymentMethodSection>

      {/* Payment Details Section */}
      <PaymentDetailsSection>
        <SectionLabel variant="subtitle2">PAYMENT DETAILS</SectionLabel>
        <PaymentDetailsList>
          <DetailRow>
            <Typography variant="h6">Date</Typography>
            <DateValue variant="h6">{formatDateForDisplay(paymentDate)}</DateValue>
          </DetailRow>

          <AmountInputContainer>
            <Typography variant="h6">Amount</Typography>
            <AmountInput
              type="text"
              value={paymentAmount}
              onChange={handlePaymentAmountChange}
              placeholder="20.00"
              disabled={isLoading}
              error={!!error}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography
                      sx={(theme) => ({
                        color: isLoading ? theme.palette.action.disabled : theme.palette.text.secondary,
                        fontSize: '17px',
                        fontWeight: 500,
                        lineHeight: '22px',
                      })}
                    >
                      $
                    </Typography>
                  </InputAdornment>
                ),
              }}
              inputProps={{
                inputMode: 'decimal',
                pattern: '[0-9]*\\.?[0-9]*',
              }}
            />
          </AmountInputContainer>
        </PaymentDetailsList>

        {error && (
          <ErrorContainer>
            <ErrorText variant="caption">{error}</ErrorText>
          </ErrorContainer>
        )}
      </PaymentDetailsSection>

      {/* Save Button */}
      <Button fullWidth variant="primary" onClick={handleSaveClick} disabled={isLoading || !paymentAmount}>
        {isLoading ? (
          <LoadingContainer>
            <CircularProgress size={20} color="inherit" />
            <span>Processing...</span>
          </LoadingContainer>
        ) : (
          'Save'
        )}
      </Button>
    </DrawerContentContainer>
  );
};

RecordPaymentDrawer.propTypes = {
  employee: PropTypes.shape({
    id: PropTypes.string.isRequired,
    displayName: PropTypes.string,
    profileImageUrl: PropTypes.string,
    shiftTypes: PropTypes.arrayOf(PropTypes.string),
    tipsOwed: PropTypes.number,
  }).isRequired,
  onConfirm: PropTypes.func.isRequired,
  handleCloseDrawer: PropTypes.func.isRequired,
};

export default RecordPaymentDrawer;
